<script>
  import { format } from 'svelte-i18n';
  import { PdfViewer } from 'svelte-pdf-simple';
  import { Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Switch } from '@client/components/switch';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  import { formatBytes } from '@common/utils/numbers';

  export let rulename = '';
  export let files = [];
  export let viewonly = false;
  export let noncompliant = false;
  export let invalidRulename = false;
  export let currentRulenames = [];
  export let pdfViewer = null;
  export let pageNumber = 0;
  export let totalPages = 0;
  export let isPdfLoaded = false;
  export let onRemoveFile = () => {};
  export let goToPage = () => {};
  export let handlePageChanged = () => {};
  export let handleLoadedSuccess = () => {};
  export let inputId = 'document';
</script>

<div class="document-upload-form">
  <div class="section">
    <div class="heading">
      {$format('label.NAME')} <Icon name="help-circle" id="rulename-info" class="cursor-help h5" />
    </div>
    <Tooltip target="rulename-info" placement="top">{$format('label.TOOLTIP.ASSET_DOCUMENT_RULENAME')}</Tooltip>
    <Input
      type="text"
      class="rulename"
      placeholder={$format('label.PLACEHOLDER.INPUT', {
        values: { param: $format('label.NAME').toLowerCase() }
      })}
      invalid={invalidRulename}
      feedback={$format('label.VALIDATION_NAME_EXISTS', {
        values: { type: $format('label.DOCUMENT'), param: $format('label.NAME').toLowerCase() }
      })}
      on:keyup={() => {
        rulename = sanitizeRulenameOnKeyUp(rulename);
      }}
      bind:value={rulename}
    />
  </div>
  <div class="section">
    <div class="heading">{$format('label.DOCUMENT_DETAILS')}</div>
    <div class="asset-details">
      <div class="asset-item">
        <div class="file">
          <div class="asset-info">
            <b>{$format('label.UPLOAD_FILE')}</b>
            <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_UPLOAD_FILE')}</p>
          </div>
          <div class="asset-actions">
            <label class="btn btn-secondary btn-sm" for={inputId}>{$format('label.CHOOSE_FILE')}</label>
            <span id="file-chosen">
              {#if !files.length}
                {$format('label.NO_FILE_CHOSEN')}
              {/if}
            </span>
            <input id={inputId} name={inputId} hidden type="file" bind:files accept=".pdf" />
          </div>
        </div>
      </div>
      {#if files.length}
        <div class="asset-item preview">
          <div class="asset-info">
            <b>{$format('label.PREVIEW')}</b>
          </div>
          <div class="asset-actions">
            <div class="document-file">
              {#each Array.from(files) as file, index}
                <div class="file-item">
                  <div class="document-preview">
                    <a href={URL.createObjectURL(file)} target="_blank">
                      <PdfViewer
                        bind:this={pdfViewer}
                        props={{
                          path: URL.createObjectURL(file),
                          withAnnotations: true,
                          withTextContent: true
                        }}
                        on:load_success={handleLoadedSuccess}
                        on:page_changed={handlePageChanged}
                        style="width: 148px;"
                      />
                    </a>
                    {#if isPdfLoaded && totalPages > 1}
                      <span
                        >{$format('label.DOCUMENT_MODAL_PREVIEW_NAVIGATION', {
                          values: { current: pageNumber, total: totalPages }
                        })}</span
                      >
                      <div class="navigation-icons">
                        <Icon
                          name="arrow-left"
                          class="pointer {pageNumber === 1 ? 'opacity-50 pe-none' : ''}"
                          on:click={() => goToPage(pageNumber - 1)}
                          role="button"
                          tabindex="0"
                        />
                        <Icon
                          name="arrow-right"
                          class="pointer {pageNumber === totalPages ? 'opacity-50 pe-none' : ''}"
                          on:click={() => goToPage(pageNumber + 1)}
                          role="button"
                          tabindex="0"
                        />
                      </div>
                    {/if}
                  </div>
                  {file.name} ({formatBytes(file.size)})
                  <div class="icon-actions">
                    <Icon name="trash-2" class="pointer" on:click={onRemoveFile} role="button" tabindex="0" />
                  </div>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {/if}
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.VIEW_ONLY')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_OPTION_VIEW_ONLY')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={viewonly} />
        </div>
      </div>
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.NON_COMPLIANT')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_DOCUMENT_OPTION_NON_COMPLIANT')}</p>
        </div>
        <div class="asset-actions">
          <Switch bind:checked={noncompliant} />
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .document-upload-form {
    width: 100%;

    .section {
      &:first-child {
        margin-top: var(--margin-base);
      }

      + .section {
        margin-top: var(--margin-xlarge);
      }

      .heading {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }

    .asset-details {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-small) var(--padding-base);

      .asset-item {
        display: flex;
        padding: var(--padding-large);

        &.preview {
          display: flex;
          flex-direction: column;

          .document-preview {
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius-base);
            display: flex;
            flex-direction: column;
            height: auto;
            width: 150px;

            span {
              color: var(--color-gray-600);
              display: flex;
              font-size: var(--font-size-medium);
              justify-content: center;
              margin-top: var(--margin-base);
            }

            .navigation-icons {
              align-items: center;
              display: flex;
              gap: 20px;
              justify-content: center;
              margin: var(--margin-small) 0 var(--margin-base);
            }
          }
        }

        + .asset-item {
          border-top: 1px solid var(--border-color);
        }

        .file {
          display: flex;
          flex: 1;
        }

        .asset-info {
          align-items: center;
          flex: 1;

          b {
            color: var(--color-gray-900);
          }
        }

        .asset-actions {
          align-items: center;
          display: flex;
          gap: 10px;

          .document-file {
            width: 100%;

            .file-item {
              align-items: center;
              display: flex;
              gap: 16px;
              padding: var(--padding-large) 0 0 0;

              .icon-actions {
                display: flex;
                flex: 1;
                justify-content: end;
              }
            }
          }
        }
      }
    }

    :global(.invalid-feedback) {
      color: var(--color-red-500);
      font-size: var(--font-size-medium);
      margin-top: var(--margin-base);
    }
  }

  @media only screen and (max-width: 800px) {
    .document-upload-form {
      .asset-details {
        .asset-item {
          display: flex;
          flex-direction: column;
          gap: 16px;

          .file {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
        }
      }
    }
  }
</style>
